import { Fragment, h } from '@stencil/core';

import { QUESTION_ODORS } from '../../../utils/utils.question';

const sectionHeaderClass = { 'font-open-sans mt-6 pb-6': true };
const renderDataGrouping = (options = [], heading = '', highlightLabel = false) => {
  const hasOptions = options.length > 0;
  const labelClass = {
    'font-bold': highlightLabel,
  };

  return (
    <div>
      {options.map(
        ({ label, value }) =>
          value && (
            <div>
              <span class={labelClass}>{label}:</span> {value}
            </div>
          )
      )}
      {!hasOptions && <div>No {heading.toLowerCase()} entered</div>}
    </div>
  );
};

const renderNestedDataGrouping = (options = [], heading = '') => {
  const hasOptions = options.length > 0;
  return (
    <div>
      {options.map(({ label, value, comment }) => {
        return (
          value && (
            <div>
              {label && <span>{label}:</span>}
              <ul class="list-disc pb-2">
                <li>
                  {value}
                  {comment && (
                    <ul class="list-[circle]">
                      <li>Explanation: {comment}</li>
                    </ul>
                  )}
                </li>
              </ul>
            </div>
          )
        );
      })}
      {!hasOptions && `No ${heading.toLowerCase()} entered`}
    </div>
  );
};

const renderConditions = (vehicleConditions = []) => {
  return vehicleConditions.map((con) => (
    <div>
      <h4 class="mt-4">{con.header}</h4>
      {con.answers.map((ans) => {
        // TODO: Instead of questionType checking here, we should probably abstract this out and model it more closely to the view, not the data
        if (ans.questionType === 'MultiSelect') {
          return (
            <div>
              {ans.itemHeader}: <br />
              {renderNestedDataGrouping(ans.answers, con.header)}
            </div>
          );
        } else if (ans.questionType === 'SingleSelect') {
          const hasComments = ans.answers.some((ch) => ch.comment);
          return hasComments ? renderNestedDataGrouping(ans.answers, con.header) : renderDataGrouping(ans.answers, con.header);
        } else if (ans.questionType === 'ToggleSelect') {
          return ans.answers.map((item) => {
            return item.comment.length > 0 ? renderNestedDataGrouping(ans.answers, con.header) : renderDataGrouping(ans.answers, con.header);
          });
        }
        return renderDataGrouping(ans.answers, con.header);
      })}
    </div>
  ));
};

const renderModifications = (vehicleModifications = []) => {
  if (vehicleModifications.length > 0) {
    return vehicleModifications.map((mod) => (
      <div>
        <h4 class="mt-4">{mod.header}</h4>
        {mod.answers.map((ans) => {
          const hasComments = ans.answers.some((ans) => ans.comment !== '');
          return hasComments || ans.questionType === 'MultiSelect' ? renderNestedDataGrouping(ans.answers, mod.header) : renderDataGrouping(ans.answers, mod.header);
        })}
      </div>
    ));
  }
  return (
    <ul class="m-0 p-0 list-none">
      <li>No vehicle modification information entered</li>
    </ul>
  );
};

const renderVehicleHistory = (autoCheck, vehicle, vehicleHistory = []) => {
  const { hasAccidents, hasSingleOwner, autoCheckFullReportLink } = autoCheck;
  const { isAuctionedLast45, isRental } = vehicle;
  const vehicleHistoryOptions = [
    { label: 'Original Owner', value: hasSingleOwner ? 'Yes' : 'No' },
    { label: 'Accidents', value: hasAccidents ? 'Yes' : 'No' },
    { label: 'Auctioned in Last 45 Days', value: isAuctionedLast45 ? 'Yes' : 'No' },
    { label: 'Rental Vehicle', value: isRental ? 'Yes' : 'No' },
  ];
  const historyNoOdors = vehicleHistory.filter((q) => q.questionId !== QUESTION_ODORS);
  const optionList = autoCheckFullReportLink.length > 1 ? vehicleHistoryOptions : historyNoOdors;
  return (
    <div class={sectionHeaderClass}>
      <h3 class="mb-2">Vehicle History</h3>
      {renderDataGrouping(optionList, 'Vehicle history')}
    </div>
  );
};

const getVehicleReplacementOptions = ({ hasReplacement, replacementTypes, replacementName, replacementVehicleYearMin, replacementVehicleYearMax }) => {
  if (hasReplacement) {
    return [
      { label: 'Looking For', value: replacementTypes },
      { label: 'Vehicle', value: replacementName },
      { label: 'Year', value: `${replacementVehicleYearMin} - ${replacementVehicleYearMax}` },
    ];
  }
  return [];
};

export const OfferDetailsModal = (
  { shouldShowDealerComments, autoCheck, customer, offer, vehicle } = {
    shouldShowDealerComments: false,
    autoCheck: {
      autoCheckFullReportLink: '',
      hasAccidents: false,
      hasPriorRentals: true,
      hasSingleOwner: true,
      reportDate: '',
    },
    customer: { email: '', firstName: '', lastName: '', phone: '', zip: '' },
    offer: {
      dealerComment: '',
      vehicleConditions: [],
      vehicleHistory: [],
      vehicleOdor: [],
      vehicleModifications: [],
    },
    vehicle: {
      color: '',
      drivetrain: '',
      engine: '',
      fullName: '',
      hasReplacement: false,
      hasTwoSetsOfKeys: true,
      isAuctionedLast45: false,
      isRental: false,
      mileage: 0,
      offerOptions: [],
      replacementName: '',
      replacementTypes: '',
      replacementVehicleYearMax: 2023,
      replacementVehicleYearMin: 2018,
      transmission: '',
      vin: '',
      yearMakeModel: '',
    },
  }
) => {
  const { email, firstName, lastName, phone, zip } = customer;
  const contactOptions = [
    { label: 'Name', value: `${firstName} ${lastName}` },
    { label: 'Email', value: email },
    { label: 'Phone', value: phone },
    { label: 'Zipcode', value: zip },
  ];

  const { color, engine, drivetrain, fullName, hasTwoSetsOfKeys, mileage, transmission, vin } = vehicle;
  const vehicleDescOptions = [
    { label: 'VIN', value: vin },
    { label: 'Vehicle', value: fullName },
    { label: 'Mileage', value: mileage },
    { label: 'Color', value: color },
    { label: '2 Sets of Keys', value: hasTwoSetsOfKeys ? 'Yes' : 'No' },
  ];

  const vehicleOptions = [
    { label: 'Engine', value: engine },
    { label: 'Transmission', value: transmission },
    { label: 'Drive', value: drivetrain },
    { label: 'Installed Options', value: ' ' },
  ];

  const SectionSeparator = () => <hr class="border-0 bg-clrs-grey h-px" />;

  return [
    <ico-modal id="offer-details" slot="modal" displayX={true}>
      <div slot="header">
        <h3 class="font-montserrat font-bold text-xl">Offer Details</h3>
        <div>Please review the information you've given us.</div>
      </div>

      <div slot="main">
        <div class={sectionHeaderClass}>
          <h3 class="mb-2">Contact Information</h3>
          {renderDataGrouping(contactOptions, 'Contact Information', true)}
        </div>

        <SectionSeparator />

        <div class={sectionHeaderClass}>
          <h3 class="mb-2">Vehicle Description</h3>
          {renderDataGrouping(vehicleDescOptions, 'Vehicle Description', true)}
        </div>

        <SectionSeparator />

        <div class={sectionHeaderClass}>
          <h3 class="mb-2">Vehicle Options</h3>
          {renderDataGrouping(vehicleOptions, 'Vehicle Options', true)}
          <ul class="columns-2 list-none p-0">
            {vehicle.offerOptions.map((o) => (
              <li>{o}</li>
            ))}
          </ul>
        </div>

        <SectionSeparator />

        <div class={sectionHeaderClass}>
          <h3 class="mb-2">Vehicle Modifications</h3>
          {renderModifications(offer.vehicleModifications)}
        </div>

        <SectionSeparator />

        {renderVehicleHistory(autoCheck, vehicle, offer.vehicleHistory)}

        <SectionSeparator />

        <div class={sectionHeaderClass}>
          <h3 class="mb-2">Condition</h3>
          <div>
            <h4 class="mt-4">Interior Wear or Damage</h4>
            {renderDataGrouping(offer.vehicleOdor, 'Interior Wear or Damage')}
          </div>
          {renderConditions(offer.vehicleConditions)}
        </div>

        <SectionSeparator />

        <div class={sectionHeaderClass}>
          <h3 class="mb-2">Replacement Vehicle</h3>
          {renderDataGrouping(getVehicleReplacementOptions(vehicle), 'Replacement Vehicle', true)}
        </div>

        {shouldShowDealerComments && (
          <Fragment>
            <SectionSeparator />
            <div class={sectionHeaderClass}>
              <h3 class="mb-2">Additional Comments</h3>
              <p>{offer.dealerComment.length > 0 ? offer.dealerComment : 'No comments entered.'}</p>
            </div>
          </Fragment>
        )}
      </div>
    </ico-modal>,
  ];
};
